import { FormEvent, useEffect, useMemo, useState } from "react";
import { backendFunctionsEnabled, loadOrders, queuePrintJob, submitCheckout, updateOrderState } from "./api";
import {
  adminRoles,
  login,
  loginWithGoogle,
  logout,
  observeSession,
  refreshVerificationStatus,
  register,
  requestPasswordReset,
  resendVerificationEmail,
  type Session,
} from "./auth";
import { demoOrders, products as demoProducts } from "./demoData";
import { AdminCms } from "./AdminCms";
import { defaultSiteContent, subscribeCatalog, subscribeSiteContent } from "./cms";
import type { AppView, Audience, CartLine, DocumentKind, Order, PrintFormat, Product, SiteContent } from "./domain";
import { firebaseConfigured } from "./firebase";

const audienceLabels: Record<Audience, string> = {
  customer: "Konsumen",
  reseller: "Reseller",
  partner: "Mitra B2B",
};

const isProduction = import.meta.env.VITE_APP_ENV === "production";
const onlineCheckoutEnabled = import.meta.env.VITE_ENABLE_ONLINE_CHECKOUT === "true";
const whatsappNumber = "6282155584449";

const statusLabels: Record<Order["status"], string> = {
  new: "Pesanan baru",
  confirmed: "Dikonfirmasi",
  preparing: "Disiapkan",
  ready: "Siap dikirim",
  completed: "Selesai",
};

const priceReviewLabels: Record<Order["priceReviewStatus"], string> = {
  pending_admin_confirmation: "Menunggu konfirmasi admin",
  confirmed: "Harga sudah dikonfirmasi",
};

function money(value: number): string {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value);
}

function productPrice(product: Product, variant: string, audience: Audience): number {
  return product.variantPrices?.[variant]?.[audience] ?? product.demoPrices[audience];
}

function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <div className={`brand ${inverse ? "brand-inverse" : ""}`}>
      <img className="brand-mark" src="/assets/brand/alabian-food-logo.png" alt="" aria-hidden="true" />
      <div><strong>Alabian Kebab</strong><span>Beda dari yang lain</span></div>
    </div>
  );
}

function ProductCard({ product, audience, onAdd }: { product: Product; audience: Audience; onAdd: (product: Product, variant: string) => void }) {
  const [variant, setVariant] = useState(product.variants[0]);
  return (
    <article className="product-card">
      <div className={`product-photo ${product.imageUrl ? "has-image" : ""}`} role="img" aria-label={`Foto asli ${product.name}`}>
        {product.imageUrl ? <img src={product.imageUrl} alt={product.name} loading="lazy" /> : <span>Foto produk asli</span>}
      </div>
      <div className="product-copy">
        <p className="eyebrow">Premium frozen kebab</p>
        <h3>{product.name}</h3>
        <p>{product.description}</p>
        <label>
          Ukuran
          <select value={variant} onChange={(event) => setVariant(event.target.value)}>
            {product.variants.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <div className="product-footer">
          <div><span>Harga {audienceLabels[audience]}</span><strong>{money(productPrice(product, variant, audience))}</strong></div>
          <button type="button" disabled={onlineCheckoutEnabled && product.stock === 0 && firebaseConfigured} onClick={() => onAdd(product, variant)}>{onlineCheckoutEnabled && product.stock === 0 && firebaseConfigured ? "Stok habis" : "Tambah"}</button>
        </div>
      </div>
    </article>
  );
}

const documentLabels: Record<DocumentKind, string> = {
  invoice: "NOTA PESANAN",
  purchase_order: "PURCHASE ORDER",
  delivery_note: "SURAT JALAN",
  label: "LABEL PESANAN",
};

function PrintDocument({ order, format, kind }: { order: Order; format: PrintFormat; kind: DocumentKind }) {
  const showAmount = kind === "invoice" || kind === "purchase_order";
  return (
    <section className={`print-document print-${format}`} aria-label="Pratinjau dokumen cetak">
      <header><Brand /><div><strong>{documentLabels[kind]}</strong><span>{order.number}</span></div></header>
      <div className="print-meta">
        <div><span>Pelanggan</span><strong>{order.customerName}</strong></div>
        <div><span>Kategori</span><strong>{audienceLabels[order.customerType]}</strong></div>
        <div><span>WhatsApp</span><strong>{order.customerPhone || "-"}</strong></div>
        <div><span>Alamat pengiriman</span><strong>{order.shippingAddress || "-"}</strong></div>
        <div><span>Status</span><strong>{statusLabels[order.status]}</strong></div>
        <div><span>Pembayaran</span><strong>{order.paymentStatus}</strong></div>
      </div>
      <table>
        <thead><tr><th>Produk</th><th>Qty</th>{showAmount && <th>Jumlah</th>}</tr></thead>
        <tbody>
          {order.lines.map((line) => (
            <tr key={`${line.productId}-${line.variant}`}><td>{line.productName}<small>{line.variant}</small></td><td>{line.quantity}</td>{showAmount && <td>{money(line.quantity * line.unitPrice)}</td>}</tr>
          ))}
        </tbody>
        {showAmount && <tfoot>
          <tr><th colSpan={2}>Subtotal</th><th>{money(order.subtotal)}</th></tr>
          {order.shippingTotal > 0 && <tr><th colSpan={2}>Ongkir</th><th>{money(order.shippingTotal)}</th></tr>}
          {order.discountTotal > 0 && <tr><th colSpan={2}>Diskon</th><th>-{money(order.discountTotal)}</th></tr>}
          <tr><th colSpan={2}>Total</th><th>{money(order.total)}</th></tr>
        </tfoot>}
      </table>
      <div className="print-qr">QR<br /><small>{order.number}</small></div>
      <footer>Terima kasih telah mempercayai Alabian Kebab.</footer>
    </section>
  );
}

export default function App() {
  const [view, setView] = useState<AppView>("store");
  const [audience, setAudience] = useState<Audience>("customer");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [orders, setOrders] = useState<Order[]>(demoOrders);
  const [selectedOrderId, setSelectedOrderId] = useState(demoOrders[0].id);
  const [printFormat, setPrintFormat] = useState<PrintFormat>("a4");
  const [documentKind, setDocumentKind] = useState<DocumentKind>("invoice");
  const [printMessage, setPrintMessage] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register" | "forgot">("login");
  const [authMessage, setAuthMessage] = useState("");
  const [catalog, setCatalog] = useState<Product[]>(demoProducts);
  const [siteContent, setSiteContent] = useState<SiteContent>(defaultSiteContent);
  const [adminSection, setAdminSection] = useState<"orders" | "cms">("orders");
  const [orderControl, setOrderControl] = useState({
    status: demoOrders[0].status,
    paymentStatus: demoOrders[0].paymentStatus,
    priceReviewStatus: demoOrders[0].priceReviewStatus,
    shippingTotal: demoOrders[0].shippingTotal,
    discountTotal: demoOrders[0].discountTotal,
  });

  useEffect(() => observeSession(setSession), []);
  useEffect(() => subscribeCatalog(session, setCatalog), [session]);
  useEffect(() => subscribeSiteContent(setSiteContent), []);
  useEffect(() => {
    void loadOrders(session).then((items) => {
      setOrders(items);
      if (items[0]) setSelectedOrderId((current) => items.some((item) => item.id === current) ? current : items[0].id);
    }).catch(() => setOrders([]));
  }, [session]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [view]);

  const cartTotal = useMemo(() => cart.reduce((total, line) => total + line.unitPrice * line.quantity, 0), [cart]);
  const selectedOrder = orders.find((order) => order.id === selectedOrderId) ?? orders[0];
  const canOperate = !firebaseConfigured || Boolean(session && adminRoles.includes(session.role));
  useEffect(() => {
    if (!selectedOrder) return;
    setOrderControl({
      status: selectedOrder.status,
      paymentStatus: selectedOrder.paymentStatus,
      priceReviewStatus: selectedOrder.priceReviewStatus,
      shippingTotal: selectedOrder.shippingTotal,
      discountTotal: selectedOrder.discountTotal,
    });
  }, [selectedOrder?.id, selectedOrder?.status, selectedOrder?.paymentStatus, selectedOrder?.priceReviewStatus, selectedOrder?.shippingTotal, selectedOrder?.discountTotal]);

  function addProduct(product: Product, variant: string) {
    setCart((current) => {
      const match = current.find((line) => line.productId === product.id && line.variant === variant);
      if (match) return current.map((line) => line === match ? { ...line, quantity: line.quantity + 1 } : line);
      return [...current, { productId: product.id, productName: product.name, variant, quantity: 1, unitPrice: productPrice(product, variant, audience) }];
    });
  }

  function changeQuantity(index: number, delta: number) {
    setCart((current) => current.flatMap((line, lineIndex) => {
      if (lineIndex !== index) return [line];
      const quantity = line.quantity + delta;
      return quantity > 0 ? [{ ...line, quantity }] : [];
    }));
  }

  async function handleCheckout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (firebaseConfigured && !session) {
      setCheckoutMessage("Login diperlukan sebelum membuat pesanan.");
      setAuthOpen(true);
      return;
    }
    if (firebaseConfigured && session && !session.emailVerified) {
      setCheckoutMessage("Verifikasi alamat email terlebih dahulu sebelum membuat pesanan.");
      return;
    }
    setCheckoutMessage("Mengirim pesanan...");
    try {
      const result = await submitCheckout({
        name: String(form.get("name")), phone: String(form.get("phone")), address: String(form.get("address")), audience, lines: cart,
      });
      if (!backendFunctionsEnabled) {
        const lines = cart.map((line) => `- ${line.productName} (${line.variant}) x${line.quantity} = ${money(line.unitPrice * line.quantity)}`).join("\n");
        const message = [
          `Halo Kak Iyan, saya sudah membuat pesanan ${result.orderNumber}:`,
          lines,
          `Estimasi total: ${money(cartTotal)}`,
          `Kategori: ${audienceLabels[audience]}`,
          `Nama: ${String(form.get("name"))}`,
          `WhatsApp: ${String(form.get("phone"))}`,
          `Alamat: ${String(form.get("address"))}`,
          "Mohon konfirmasi stok dan total final.",
        ].join("\n");
        window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
      }
      setCheckoutMessage(`Pesanan ${result.orderNumber} tersimpan. ${backendFunctionsEnabled ? "Pesanan masuk ke admin." : "Kirim pesan WhatsApp yang terbuka untuk konfirmasi final."}`);
      const refreshed = await loadOrders(session);
      setOrders(refreshed);
      setCart([]);
    } catch {
      setCheckoutMessage("Pesanan belum dapat disimpan. Periksa koneksi, status verifikasi email, lalu coba lagi.");
    }
  }

  async function handlePrint() {
    if (!selectedOrder) return;
    document.documentElement.dataset.printFormat = printFormat;
    if (!canOperate) {
      setPrintMessage(`Dokumen siap dicetak sebagai ${printFormat}.`);
      window.print();
      return;
    }
    setPrintMessage("Menyiapkan print job...");
    try {
      await queuePrintJob(selectedOrder.id, printFormat);
      setOrders((current) => current.map((order) => order.id === selectedOrder.id ? { ...order, printStatus: "printed" } : order));
      setPrintMessage(`Siap dicetak sebagai ${printFormat}.`);
      window.print();
    } catch {
      setPrintMessage("Print job gagal dibuat.");
    }
  }

  async function handleAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setAuthMessage(authMode === "login" ? "Memeriksa akun..." : authMode === "register" ? "Membuat akun..." : "Mengirim tautan reset...");
    try {
      if (authMode === "forgot") {
        await requestPasswordReset(String(form.get("email")));
        setAuthMessage("Jika email terdaftar, tautan reset kata sandi sudah dikirim. Periksa inbox dan folder spam.");
        return;
      }
      const nextSession = authMode === "login"
        ? await login(String(form.get("email")), String(form.get("password")))
        : await register(String(form.get("displayName")), String(form.get("email")), String(form.get("password")), form.get("accountType") === "reseller" ? "reseller" : "customer");
      setSession(nextSession);
      setAuthMessage(nextSession.emailVerified
        ? `Selamat datang, ${nextSession.displayName}.`
        : `Akun berhasil dibuat. Tautan verifikasi telah dikirim ke ${nextSession.email}.`);
      setAuthOpen(false);
    } catch (error) {
      setAuthMessage(error instanceof Error ? error.message : "Login gagal. Periksa data akun.");
    }
  }

  async function handleGoogleLogin() {
    setAuthMessage("Membuka login Google...");
    try {
      const nextSession = await loginWithGoogle();
      setSession(nextSession);
      setAuthMessage(`Selamat datang, ${nextSession.displayName}.`);
      setAuthOpen(false);
    } catch (error) {
      setAuthMessage(error instanceof Error ? error.message : "Login Google belum berhasil.");
    }
  }

  async function handleResendVerification() {
    setAuthMessage("Mengirim ulang email verifikasi...");
    try {
      await resendVerificationEmail();
      setAuthMessage(`Email verifikasi dikirim ulang ke ${session?.email ?? "alamat akun"}.`);
    } catch (error) {
      setAuthMessage(error instanceof Error ? error.message : "Email verifikasi belum dapat dikirim ulang.");
    }
  }

  async function handleRefreshVerification() {
    setAuthMessage("Memeriksa status verifikasi...");
    try {
      const nextSession = await refreshVerificationStatus();
      setSession(nextSession);
      setAuthMessage(nextSession.emailVerified ? "Email berhasil terverifikasi." : "Email belum terverifikasi. Buka tautan pada email lalu periksa lagi.");
    } catch (error) {
      setAuthMessage(error instanceof Error ? error.message : "Status verifikasi belum dapat diperiksa.");
    }
  }

  async function handleOrderState() {
    if (!selectedOrder) return;
    setPrintMessage("Menyimpan status pesanan...");
    try {
      const total = await updateOrderState(selectedOrder.id, { ...orderControl, subtotal: selectedOrder.subtotal });
      setOrders((current) => current.map((order) => order.id === selectedOrder.id ? { ...order, ...orderControl, total } : order));
      setPrintMessage("Konfirmasi pesanan berhasil disimpan.");
    } catch (error) {
      setPrintMessage(error instanceof Error ? error.message : "Status pesanan gagal diperbarui.");
    }
  }

  return (
    <div className="app-shell">
      {!isProduction && <div className="demo-banner">MODE DEVELOPMENT — transaksi dan data dapat menggunakan layanan uji</div>}
      <header className="site-header">
        <a href="#top" aria-label="Alabian Kebab beranda"><Brand /></a>
        <nav aria-label="Navigasi utama">
          <button className={view === "store" ? "active" : ""} onClick={() => setView("store")}>Website</button>
          <button className={view === "orders" ? "active" : ""} onClick={() => setView("orders")}>Pesanan</button>
          {canOperate && <button className={view === "admin" ? "active" : ""} onClick={() => setView("admin")}>Admin & Cetak</button>}
        </nav>
        <div className="header-actions">
          <button className="account-button" onClick={() => {
            if (session) void logout();
            else setAuthOpen(true);
          }}>{session ? `${session.displayName} · Keluar` : "Masuk"}</button>
          <button className="cart-button" onClick={() => { setView("store"); setCheckoutOpen(true); }}>Keranjang <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span></button>
        </div>
      </header>

      {!firebaseConfigured ? <div className="connection-banner">Mode lokal aktif. Sambungkan konfigurasi Firebase untuk akun dan data real-time.</div> : <>
        {!isProduction && <div className="connection-banner connected">Firebase development tersambung.{authMessage && !authOpen && <span>{authMessage}</span>}</div>}
        {session && !session.emailVerified && <div className="verification-banner"><span>Verifikasi email <strong>{session.email}</strong> agar akun dapat membuat pesanan.</span><div><button onClick={() => void handleResendVerification()}>Kirim ulang</button><button onClick={() => void handleRefreshVerification()}>Sudah verifikasi</button></div></div>}
      </>}

      {view === "store" && (
        <main id="top">
          <section className="hero">
            <div className="hero-copy">
              <p className="eyebrow">{siteContent.heroEyebrow}</p>
              <h1>{siteContent.heroTitle}</h1>
              <p>{siteContent.heroDescription}</p>
              <div className="hero-actions"><a className="primary-action" href="#products">{siteContent.primaryCta}</a><a className="secondary-action" href="#business">{siteContent.secondaryCta}</a></div>
              <div className="hero-points"><span>7 varian rasa</span><span>Mudah disajikan</span><span>Harga bertingkat</span></div>
            </div>
            <div className="hero-photo"><img src="/assets/products/meat-texture.jpg" alt="Frozen Kebab Alabian dengan tekstur daging asli" fetchPriority="high" /><div className="hero-photo-caption"><strong>Frozen Kebab Alabian</strong><span>Praktis, lezat, dan siap menjadi peluang usaha.</span></div></div>
          </section>

          <section className="audience-section" id="business">
            <div><p className="eyebrow">Belanja sesuai kebutuhan</p><h2>Satu produk, tiga jalur bisnis.</h2></div>
            <div className="audience-switch" role="group" aria-label="Pilih kategori pelanggan">
              {(Object.keys(audienceLabels) as Audience[]).map((item) => <button key={item} className={audience === item ? "active" : ""} onClick={() => setAudience(item)}>{audienceLabels[item]}</button>)}
            </div>
          </section>

          <section className="product-section" id="products">
            <div className="section-heading"><div><p className="eyebrow">{siteContent.catalogEyebrow}</p><h2>{siteContent.catalogTitle}</h2></div><p>Harga katalog mengacu pada daftar reseller yang diberikan Alabian. Stok dan total akhir dikonfirmasi melalui WhatsApp.</p></div>
            <div className="product-grid">{catalog.map((product) => <ProductCard key={product.id} product={product} audience={audience} onAdd={addProduct} />)}</div>
            {catalog.length === 0 && <div className="empty-state">Katalog belum diisi. Owner dapat memasukkan produk awal melalui halaman Admin.</div>}
          </section>

          <section className="pricelist-section" aria-labelledby="pricelist-title">
            <div><p className="eyebrow">Daftar harga reseller</p><h2 id="pricelist-title">Tujuh rasa, tiga pilihan isi.</h2><p>Poster harga resmi ditampilkan apa adanya. Klik gambar untuk melihat ukuran penuh.</p><a className="primary-action" href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">Tanya stok via WhatsApp</a></div>
            <a href="/assets/brand/pricelist-reseller.png" target="_blank" rel="noreferrer"><img src="/assets/brand/pricelist-reseller.png" alt="Daftar harga reseller Frozen Kebab Alabian" loading="lazy" /></a>
          </section>

          <section className="business-cta">
            <div><p className="eyebrow">{siteContent.businessEyebrow}</p><h2>{siteContent.businessTitle}</h2></div>
            <a className="primary-action" href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Halo Kak Iyan, saya tertarik mendaftar sebagai reseller Alabian Kebab.")}`} target="_blank" rel="noreferrer">{siteContent.businessCta}</a>
          </section>
        </main>
      )}

      {view === "orders" && (
        <main className="portal-page">
          <div className="page-heading"><div><p className="eyebrow">Portal pelanggan</p><h1>Pesanan saya</h1></div><button onClick={() => setView("store")}>Buat pesanan baru</button></div>
          {firebaseConfigured && !session ? <section className="access-panel"><h2>Login untuk melihat pesanan</h2><p>Riwayat pesanan hanya ditampilkan kepada pemilik akun.</p><button onClick={() => setAuthOpen(true)}>Masuk atau daftar</button></section> : <div className="order-list">
            {orders.map((order) => <article key={order.id} className="order-card"><div><span>{new Date(order.createdAt).toLocaleString("id-ID")}</span><h2>#{order.number}</h2><p>{order.lines.length} jenis produk • {money(order.total)}</p></div><div><span className={`status status-${order.status}`}>{statusLabels[order.status]}</span><button onClick={() => { setSelectedOrderId(order.id); setView("admin"); }}>Lihat dokumen</button></div></article>)}
          </div>}
        </main>
      )}

      {view === "admin" && (
        <main className="admin-page">
          <div className="page-heading"><div><p className="eyebrow">{canOperate ? "Operasional" : "Dokumen"}</p><h1>{canOperate ? (adminSection === "orders" ? "Admin order & cetak" : "Kelola website") : "Dokumen pesanan"}</h1></div>{canOperate && adminSection === "orders" && <div className="admin-metrics"><span><strong>{orders.filter((order) => order.status === "new").length}</strong> order baru</span><span><strong>{orders.filter((order) => order.printStatus === "not_printed").length}</strong> belum dicetak</span></div>}</div>
          {canOperate && <div className="admin-section-tabs"><button className={adminSection === "orders" ? "active" : ""} onClick={() => setAdminSection("orders")}>Order & cetak</button><button className={adminSection === "cms" ? "active" : ""} onClick={() => setAdminSection("cms")}>Produk & website</button></div>}
          {canOperate && adminSection === "cms" ? <AdminCms products={catalog} content={siteContent} session={session} /> : selectedOrder ? <div className="admin-layout">
            {canOperate && <section className="admin-queue">
              <h2>Antrean pesanan</h2>
              {orders.map((order) => <button key={order.id} className={selectedOrder.id === order.id ? "selected" : ""} onClick={() => setSelectedOrderId(order.id)}><span><strong>{order.number}</strong><small>{order.customerName}</small></span><span>{money(order.total)}</span></button>)}
            </section>}
            <section className="print-panel">
              {canOperate && <div className="order-state-controls">
                <label>Status order<select value={orderControl.status} onChange={(event) => setOrderControl({ ...orderControl, status: event.target.value as Order["status"] })}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label>Pembayaran<select value={orderControl.paymentStatus} onChange={(event) => setOrderControl({ ...orderControl, paymentStatus: event.target.value as Order["paymentStatus"] })}><option value="unpaid">Belum dibayar</option><option value="pending">Menunggu verifikasi</option><option value="paid">Lunas</option></select></label>
                <label>Konfirmasi harga<select value={orderControl.priceReviewStatus} onChange={(event) => setOrderControl({ ...orderControl, priceReviewStatus: event.target.value as Order["priceReviewStatus"] })}>{Object.entries(priceReviewLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label>Ongkir<input type="number" min="0" value={orderControl.shippingTotal} onChange={(event) => setOrderControl({ ...orderControl, shippingTotal: Math.max(0, Number(event.target.value)) })} /></label>
                <label>Diskon<input type="number" min="0" value={orderControl.discountTotal} onChange={(event) => setOrderControl({ ...orderControl, discountTotal: Math.max(0, Number(event.target.value)) })} /></label>
                <div className="order-total-preview"><span>Total final</span><strong>{money(Math.max(0, selectedOrder.subtotal + orderControl.shippingTotal - orderControl.discountTotal))}</strong></div>
                <button type="button" onClick={() => void handleOrderState()}>Simpan konfirmasi</button>
              </div>}
              <div className="print-toolbar">
                <label>Dokumen<select value={documentKind} onChange={(event) => { const kind = event.target.value as DocumentKind; setDocumentKind(kind); if (kind === "label") setPrintFormat("label"); }}><option value="invoice">Nota pesanan</option><option value="purchase_order">Purchase Order</option><option value="delivery_note">Surat jalan</option><option value="label">Label pesanan</option></select></label>
                <label>Format cetak<select value={printFormat} onChange={(event) => setPrintFormat(event.target.value as PrintFormat)}><option value="a4">LaserJet A4</option><option value="thermal58">Thermal 58 mm</option><option value="thermal80">Thermal 80 mm</option><option value="label">Label pesanan</option></select></label>
                <button onClick={() => void handlePrint()}>Cetak order</button>
              </div>
              {printMessage && <p className="inline-message" aria-live="polite">{printMessage}</p>}
              <PrintDocument order={selectedOrder} format={printFormat} kind={documentKind} />
            </section>
          </div> : <div className="empty-state">Belum ada pesanan. Katalog dan teks website tetap dapat dikelola melalui tab Produk & website.</div>}
        </main>
      )}

      {view === "store" && checkoutOpen && (
        <aside className="cart-drawer" aria-label="Keranjang belanja">
          <div className="drawer-head"><div><p className="eyebrow">Keranjang</p><h2>Pesanan {audienceLabels[audience]}</h2></div><button aria-label="Tutup keranjang" onClick={() => setCheckoutOpen(false)}>×</button></div>
          {cart.length === 0 ? <><div className="empty-state">Belum ada produk di keranjang.</div>{checkoutMessage && <p className="inline-message" aria-live="polite">{checkoutMessage}</p>}</> : <>
            <div className="cart-lines">{cart.map((line, index) => <div className="cart-line" key={`${line.productId}-${line.variant}`}><div><strong>{line.productName}</strong><span>{line.variant} • {money(line.unitPrice)}</span></div><div className="quantity"><button onClick={() => changeQuantity(index, -1)}>−</button><span>{line.quantity}</span><button onClick={() => changeQuantity(index, 1)}>+</button></div></div>)}</div>
            <div className="cart-total"><span>Estimasi total</span><strong>{money(cartTotal)}</strong></div>
            <form className="checkout-form" onSubmit={handleCheckout}><label>Nama<input name="name" required /></label><label>WhatsApp<input name="phone" inputMode="tel" required /></label><label>Alamat<textarea name="address" required /></label><button type="submit">{backendFunctionsEnabled ? "Kirim pesanan" : "Buat pesanan & konfirmasi WhatsApp"}</button></form>
            {checkoutMessage && <p className="inline-message" aria-live="polite">{checkoutMessage}</p>}
          </>}
        </aside>
      )}

      {authOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setAuthOpen(false); }}>
          <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
            <div className="drawer-head"><div><p className="eyebrow">Akun Alabian</p><h2 id="auth-title">{authMode === "login" ? "Masuk ke akun" : authMode === "register" ? "Daftar akun baru" : "Reset kata sandi"}</h2></div><button aria-label="Tutup login" onClick={() => setAuthOpen(false)}>×</button></div>
            <form className="checkout-form" onSubmit={handleAuth}>
              {authMode === "register" && <><label>Nama lengkap<input name="displayName" autoComplete="name" required /></label><label>Jenis akun<select name="accountType" defaultValue="customer"><option value="customer">Konsumen</option><option value="reseller">Reseller</option></select></label></>}
              <label>Email<input name="email" type="email" autoComplete="email" required /></label>
              {authMode !== "forgot" && <label>Kata sandi<input name="password" type="password" autoComplete={authMode === "login" ? "current-password" : "new-password"} minLength={6} required /></label>}
              <button type="submit">{authMode === "login" ? "Masuk" : authMode === "register" ? "Buat akun" : "Kirim tautan reset"}</button>
            </form>
            {authMode !== "forgot" && <><div className="auth-divider"><span>atau</span></div><button className="google-auth-button" type="button" onClick={() => void handleGoogleLogin()}><span aria-hidden="true">G</span>Lanjutkan dengan Google</button></>}
            {authMessage && <p className="inline-message" aria-live="polite">{authMessage}</p>}
            {authMode === "login" && <button className="auth-switch" type="button" onClick={() => { setAuthMode("forgot"); setAuthMessage(""); }}>Lupa kata sandi?</button>}
            <button className="auth-switch" type="button" onClick={() => { setAuthMode(authMode === "register" ? "login" : authMode === "forgot" ? "login" : "register"); setAuthMessage(""); }}>{authMode === "login" ? "Belum punya akun? Daftar" : "Kembali ke halaman masuk"}</button>
          </section>
        </div>
      )}

      <footer className="site-footer"><Brand inverse /><div><strong>Hubungi Alabian</strong><a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">PIC Iyan • 0821-5558-4449</a><a href="mailto:alabiankebab@gmail.com">alabiankebab@gmail.com</a><span>Bekasi 17215, Jawa Barat</span></div><div><strong>Platform</strong><span>Web • Android • Firebase</span><span>{siteContent.footerNote}</span></div></footer>
    </div>
  );
}
